import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Router, ActivatedRoute } from '@angular/router';
import { ProjectService } from '../Services/project.service';
import { LiveStreamManagerService, LiveTileMode, LiveTileState } from './services/live-stream-manager.service';
import { TokenEncryptionService } from '../Services/token-encryption.service';
import { Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { SharedService } from '../Services/sharedService';
import { MessagingServiceService } from '../Services/messaging-service.service';
import { EntitlementsService } from '../Services/entitlements.service';
import { LiveScreencastQuotaService } from '../Services/live-screencast-quota.service';
import { EntitlementKey } from '../entitlements/entitlement-keys';
import { MatDialog } from '@angular/material/dialog';
import { PlanUpgradeDialogComponent } from '../components/plan-upgrade-dialog/plan-upgrade-dialog.component';
import { CustomTimeAgoPipe } from '../time-ago.pipe';
import moment from 'moment';
import { isRustAgentUser } from '../agent-deploy/agent-deploy.config';
import { sysConfig } from '../sysConfig';

type LiveMode = 'single' | 'grid' | 'wall' | 'patrol';
type LiveMobilePanel = 'stream' | 'people';

@Component({
  selector: 'app-live-monitoring',
  templateUrl: './live-monitoring.component.html',
  styleUrls: ['./live-monitoring.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LiveMonitoringComponent implements OnInit, OnDestroy {
  private static readonly MOBILE_MAX_WIDTH_PX = 980;

  @ViewChild('monitorPanel', { static: false }) monitorPanelRef?: ElementRef<HTMLElement>;

  readonly sysConfig = sysConfig;

  mode: LiveMode = 'single';
  query = '';
  statusFilter: 'all' | 'online' | 'idle' | 'offline' | 'absent' | 'auto-logout' | 'invited' | 'live' = 'all';
  teamFilter: string = 'all';
  teamsListForFilter: any[] = [];
  isLeftPaneCollapsed = false;
  isMobileUi = false;
  mobilePanel: LiveMobilePanel = 'stream';
  private teamMemberIdsByName = new Map<string, Set<number>>();

  employees: any[] = [];
  selectedEmployeeId: number | null = null;
  private pendingAutoStartEmployeeId: number | null = null;

  // grid/wall selection
  gridSize: 2 | 4 = 4;
  wallSize: 16 = 16;
  selectedTileId: string | null = null;

  // patrol
  patrolIntervalSec: 10 | 20 | 30 | 60 = 30;
  patrolRunning = false;
  patrolSelectedEmployeeIds: number[] = [];
  isMonitorFullscreen = false;
  private patrolTimer: any;
  private patrolIndex = 0;

  // tile state stream
  tiles$ = this.streamManager.tiles$;
  stats$ = combineLatest([this.tiles$, this.streamManager.sessions$]).pipe(
    map(([tiles, sessions]) => {
      const activeTiles = Object.values(tiles).filter(t => !!t && t.status !== 'ended');
      const liveNow = activeTiles.filter(t => t.status === 'live').length;
      const connecting = activeTiles.filter(t => t.status === 'connecting').length;
      const failed = activeTiles.filter(t => t.status === 'failed').length;
      return { liveNow, connecting, failed, activeSessions: sessions.length };
    })
  );

  private sub = new Subscription();
  private beforeUnloadHandler?: (e: BeforeUnloadEvent) => void;
  private lastFbUsersSnapshot: any[] = [];
  private readonly employeeCacheTtlMs = 5 * 60 * 1000;
  private readonly screenshotRequestTimeoutMs = 20000;
  private readonly screenshotTimeoutHandles = new Map<number, ReturnType<typeof setTimeout>>();

  constructor(
    private projectService: ProjectService,
    public streamManager: LiveStreamManagerService,
    private tokenEncryption: TokenEncryptionService,
    private db: AngularFireDatabase,
    private sharedService: SharedService,
    private messagingService: MessagingServiceService,
    private customtimeAgoPipe: CustomTimeAgoPipe,
    private cdr: ChangeDetectorRef,
    private entitlementsService: EntitlementsService,
    private liveScreencastQuota: LiveScreencastQuotaService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    const params = this.route.snapshot.queryParams;
    if (params['search']) this.query = params['search'];
    if (params['status']) this.statusFilter = params['status'] as any;
    if (params['team']) this.teamFilter = params['team'];
    if (params['mode']) this.mode = params['mode'] as LiveMode;
    if (params['employeeId']) {
      this.selectedEmployeeId = Number(params['employeeId']);
      this.pendingAutoStartEmployeeId = this.selectedEmployeeId;
    }
    if (params['patrolIds']) {
      this.patrolSelectedEmployeeIds = params['patrolIds'].split(',').map(Number);
    }
    if (params['patrolInterval']) {
      this.patrolIntervalSec = Number(params['patrolInterval']) as any;
    }

    this.entitlementsService.syncFromSession();
    this.sub.add(this.liveScreencastQuota.refresh().subscribe());
    this.syncMobileUi();
    if (!this.isMobileUi) {
      try {
        this.isLeftPaneCollapsed = localStorage.getItem('liveMonitoring.leftPaneCollapsed') === 'true';
      } catch {
        this.isLeftPaneCollapsed = false;
      }
    } else {
      this.isLeftPaneCollapsed = false;
      this.mobilePanel = 'stream';
    }

    // Fast path: hydrate from shared in-memory list / local cache first,
    // then refresh from API in background.
    this.bootstrapEmployeesFast();

    // Tabs/Employees call resetData() on destroy and tear down the global
    // screenshot ack listener. Re-arm it here so Profile → Live Monitoring
    // still gets toast + audit attach (same as Dashboard/Employees enter).
    this.messagingService.showNotification();

    this.sub.add(
      this.projectService.getUserList(0, 0).subscribe((users: any) => {
        this.applyEmployeeList(users);
      })
    );

    // Keep team options aligned with Dashboard source-of-truth team list.
    this.sub.add(
      this.projectService.getTeamList().subscribe((teams: any) => {
        this.teamsListForFilter = Array.isArray(teams) ? teams : [];
        this.rebuildTeamMembershipIndex();
        if (this.employees?.length) {
          this.applyEmployeeList(this.employees);
        }
      })
    );

    // Enrich list with Firebase presence, same way Dashboard/Employees do.
    this.sub.add(
      this.messagingService.getUserData().subscribe((fbUsers: any[]) => {
        this.lastFbUsersSnapshot = Array.isArray(fbUsers) ? fbUsers : [];
        this.applyFbPresenceSnapshot(this.lastFbUsersSnapshot);
      })
    );

    // Recompute statuses periodically so "online" naturally falls to offline if no heartbeats.
    this.sub.add(
      this.sharedService.statusCheckTrigger.subscribe(() => {
        this.applyFbPresenceSnapshot(this.lastFbUsersSnapshot);
      })
    );
    this.sharedService.timerCheckStatus();

    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange as EventListener);

    // Best-effort cleanup: close streams if tab/window is closed.
    this.beforeUnloadHandler = () => {
      try {
        this.stopPatrol();
        this.streamManager.stopAllStreams('browser_unload');
      } catch { }
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    this.enforceSingleModeOnly();
  }

  /** Grid, wall, and patrol require live_wall (Professional+). */
  canUseAdvancedLiveModes(): boolean {
    return this.entitlementsService.hasCompany(EntitlementKey.live_wall);
  }

  isAdvancedLiveModeLocked(mode: LiveMode): boolean {
    return mode !== 'single' && !this.canUseAdvancedLiveModes();
  }

  private promptAdvancedLiveUpgrade(modeLabel: string): void {
    const ref = this.dialog.open(PlanUpgradeDialogComponent, {
      width: '420px',
      maxWidth: '94vw',
      data: {
        title: 'Upgrade for multi-view',
        message: `${modeLabel} is included with Live Wall on Professional and above.`,
      },
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        void this.router.navigate(['/billings'], { queryParams: { intent: 'upgrade' } });
      }
    });
  }

  private enforceSingleModeOnly(): void {
    if (this.canUseAdvancedLiveModes()) {
      return;
    }
    if (this.mode === 'single') {
      return;
    }
    this.stopPatrol();
    this.streamManager.stopAllStreams('mode_changed');
    this.mode = 'single';
    this.selectedTileId = null;
    this.cdr.markForCheck();
  }

  private advancedModeLabel(mode: LiveMode): string {
    if (mode === 'grid') return 'Grid view';
    if (mode === 'wall') return 'Video wall';
    return 'Patrol mode';
  }

  ngOnDestroy(): void {
    void this.exitMonitorFullscreenIfActive();
    this.clearAllScreenshotRequestTimeouts();
    this.stopPatrol();
    // Close everything when leaving page
    this.streamManager.stopAllStreams('page_closed');
    if (this.beforeUnloadHandler) {
      try { window.removeEventListener('beforeunload', this.beforeUnloadHandler); } catch { }
    }
    try {
      document.removeEventListener('fullscreenchange', this.onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange as EventListener);
    } catch { }
    this.sub.unsubscribe();
  }

  private getFullscreenElement(): Element | null {
    const doc = document as Document & {
      webkitFullscreenElement?: Element | null;
    };
    return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
  }

  private onFullscreenChange = () => {
    const panel = this.monitorPanelRef?.nativeElement;
    const fs = this.getFullscreenElement();
    this.isMonitorFullscreen = !!panel && !!fs && (fs === panel || panel.contains(fs as Node));
    this.cdr.markForCheck();
  };

  /** Leave browser fullscreen when it was requested on the monitor panel. */
  private async exitMonitorFullscreenIfActive(): Promise<void> {
    const panel = this.monitorPanelRef?.nativeElement;
    const fs = this.getFullscreenElement();
    if (!fs) {
      this.isMonitorFullscreen = false;
      return;
    }
    if (panel && fs !== panel && !panel.contains(fs as Node)) {
      return;
    }
    try {
      const doc = document as Document & { webkitExitFullscreen?: () => Promise<void> };
      if (doc.exitFullscreen) {
        await doc.exitFullscreen();
      } else if (doc.webkitExitFullscreen) {
        await doc.webkitExitFullscreen();
      }
    } catch {
      // Ignore browser-specific fullscreen API failures.
    }
    this.isMonitorFullscreen = !!this.getFullscreenElement();
    this.cdr.markForCheck();
  }

  async toggleMonitorFullscreen() {
    const target = this.monitorPanelRef?.nativeElement as (HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void>;
    }) | undefined;
    if (!target) return;
    try {
      if (!this.getFullscreenElement()) {
        if (this.isMobileUi && this.mobilePanel !== 'stream') {
          this.mobilePanel = 'stream';
        }
        if (target.requestFullscreen) {
          await target.requestFullscreen();
        } else if (target.webkitRequestFullscreen) {
          await target.webkitRequestFullscreen();
        }
      } else {
        await this.exitMonitorFullscreenIfActive();
      }
    } catch {
      // Ignore browser-specific fullscreen API failures.
    }
  }

  get filteredEmployees(): any[] {
    const q = (this.query || '').trim().toLowerCase();
    const base = this.employees || [];
    return base.filter((u) => {
      const name = (u?.name || u?.display_name || '').toLowerCase();
      const email = (u?.email || u?.user_email || '').toLowerCase();
      const matchesQuery = !q || name.includes(q) || email.includes(q) || String(u?.id || '').includes(q);

      const t = this.getUserStatusType(u);
      let matchesStatus = true;
      if (this.statusFilter === 'all') {
        matchesStatus = true;
      } else if (this.statusFilter === 'live') {
        matchesStatus = this.isEmployeeStreaming(u);
      } else if (this.statusFilter === 'offline') {
        // Treat both "offline" and "absent" as offline for sidebar filtering so
        // the count shown on the badge matches the rows the user actually sees.
        matchesStatus = t === 'offline' || t === 'absent';
      } else {
        matchesStatus = t === this.statusFilter;
      }

      const selectedTeamNorm = String(this.teamFilter || '').trim().toLowerCase();
      const userId = Number(u?.id);
      const matchesTeam = this.teamFilter === 'all'
        ? true
        : this.teamMemberIdsByName.get(selectedTeamNorm)?.has(userId) === true;

      return matchesQuery && matchesStatus && matchesTeam;
    });
  }

  /**
   * Toggle a quick status filter from the header counters or sidebar badges.
   * Clicking the active filter again returns to "all". Also auto-expands the
   * left pane so the filtered list is immediately visible.
   */
  setStatusFilter(filter: 'all' | 'online' | 'idle' | 'offline' | 'absent' | 'auto-logout' | 'invited' | 'live') {
    if (filter !== 'all' && this.statusFilter === filter) {
      this.statusFilter = 'all';
    } else {
      this.statusFilter = filter;
    }
    this.onDropdownStatusChange();
  }

  onDropdownStatusChange() {
    if (this.isLeftPaneCollapsed && !this.isMobileUi) {
      this.isLeftPaneCollapsed = false;
      try {
        localStorage.setItem('liveMonitoring.leftPaneCollapsed', 'false');
      } catch { }
    }
    if (this.isMobileUi && this.statusFilter !== 'all') {
      void this.exitMonitorFullscreenIfActive();
      this.mobilePanel = 'people';
    }
    this.updateQueryParams();
    this.cdr.markForCheck();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.syncMobileUi();
  }

  private syncMobileUi(): void {
    const mobile = this.isMobileLayout();
    if (mobile === this.isMobileUi) return;
    this.isMobileUi = mobile;
    if (mobile) {
      this.isLeftPaneCollapsed = false;
      this.mobilePanel = 'stream';
    }
    this.cdr.markForCheck();
  }

  private isMobileLayout(): boolean {
    return typeof window !== 'undefined'
      && window.matchMedia(`(max-width: ${LiveMonitoringComponent.MOBILE_MAX_WIDTH_PX}px)`).matches;
  }

  setMobilePanel(panel: LiveMobilePanel): void {
    if (panel !== 'stream') {
      void this.exitMonitorFullscreenIfActive();
    }
    this.mobilePanel = panel;
    if (panel === 'people') {
      this.isLeftPaneCollapsed = false;
    }
    this.cdr.markForCheck();
  }

  isMobilePanelActive(panel: LiveMobilePanel): boolean {
    return !this.isMobileUi || this.mobilePanel === panel;
  }

  isStatusFilterActive(filter: 'all' | 'online' | 'idle' | 'offline' | 'absent' | 'auto-logout' | 'invited' | 'live'): boolean {
    return this.statusFilter === filter;
  }

  get teamOptions(): string[] {
    const set = new Set<string>();
    if (Array.isArray(this.teamsListForFilter) && this.teamsListForFilter.length) {
      for (const t of this.teamsListForFilter) {
        const name = t?.title?.rendered || t?.name;
        if (name) set.add(String(name));
      }
    } else {
      // Fallback when teams endpoint is unavailable.
      for (const u of (this.employees || [])) {
        const teams: any = u?.selectedTeam;
        if (!teams || teams === '-') continue;
        if (Array.isArray(teams) && teams.length) {
          set.add(String(teams[0]));
        }
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  clearFilters() {
    this.query = '';
    this.statusFilter = 'all';
    this.teamFilter = 'all';
    this.updateQueryParams();
  }

  updateQueryParams() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        search: this.query || null,
        status: this.statusFilter !== 'all' ? this.statusFilter : null,
        team: this.teamFilter !== 'all' ? this.teamFilter : null,
        mode: this.mode !== 'single' ? this.mode : null,
        employeeId: this.selectedEmployeeId || null,
        patrolIds: this.patrolSelectedEmployeeIds.length ? this.patrolSelectedEmployeeIds.join(',') : null,
        patrolInterval: this.patrolIntervalSec !== 30 ? this.patrolIntervalSec : null
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  toggleLeftPane() {
    this.isLeftPaneCollapsed = !this.isLeftPaneCollapsed;
    try {
      localStorage.setItem('liveMonitoring.leftPaneCollapsed', String(this.isLeftPaneCollapsed));
    } catch { }
  }

  getEmployeeInitials(user: any): string {
    const raw = String(user?.name || user?.display_name || user?.email || user?.id || '').trim();
    if (!raw) return '?';
    const parts = raw.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }

  showRustAgentBetaBadge(user: any): boolean {
    if (!sysConfig.rustAgentBetaBadgeEnabled || !user) {
      return false;
    }
    if (isRustAgentUser(user)) {
      return true;
    }
    const id = user?.id;
    if (id == null || id === '') {
      return false;
    }
    const fbUser = (this.lastFbUsersSnapshot || []).find((u: any) => String(u?.id) === String(id));
    return isRustAgentUser(fbUser);
  }

  get selectedEmployee(): any | null {
    const id = Number(this.selectedEmployeeId);
    if (!id) return null;
    return (this.employees || []).find((u: any) => Number(u?.id) === id) || null;
  }

  /**
   * Employee shown in the "View Mode" header strip. Always derived from the
   * tile that is actually streaming in the current mode, so the displayed
   * name can never disagree with the live screencast underneath. Falls back
   * to the sidebar-clicked employee only when nothing is being streamed yet.
   */
  get focusedEmployee(): any | null {
    const fromTile = this.getFocusedTileEmployee();
    if (fromTile) {
      // Always prefer the freshest record from the employee list (status,
      // last_application_name, etc.) but fall back to the stream's snapshot.
      const id = Number(fromTile?.id);
      const fresh = id ? (this.employees || []).find((u: any) => Number(u?.id) === id) : null;
      return fresh || fromTile;
    }
    return this.selectedEmployee;
  }

  /**
   * Resolve the employee record that the current mode is "pointing at":
   *   - single  -> tile 'single'
   *   - patrol  -> tile 'patrol' (currently rotating employee)
   *   - grid    -> focused grid tile, else first active grid tile
   *   - wall    -> focused wall tile, else first active wall tile
   */
  /** Tile id for the stream currently in focus (used for fullscreen snapshot, etc.). */
  private getFocusedStreamTileId(): string | null {
    const tiles = this.streamManager.getTilesSnapshot();
    if (!tiles) return null;

    const isActive = (id: string): boolean => {
      const t = tiles[id];
      if (!t || t.status === 'empty' || t.status === 'ended') return false;
      return !!(t.employeeId || t.employee?.id);
    };

    if (this.mode === 'single') return isActive('single') ? 'single' : null;
    if (this.mode === 'patrol') return isActive('patrol') ? 'patrol' : null;

    const layout = this.mode === 'grid'
      ? this.streamManager.gridTileIds(this.gridSize)
      : this.streamManager.wallTileIds(16);

    if (this.selectedTileId && layout.includes(this.selectedTileId) && isActive(this.selectedTileId)) {
      return this.selectedTileId;
    }
    for (const id of layout) {
      if (isActive(id)) return id;
    }
    return null;
  }

  /** Show snapshot affordance whenever the focused tile has an active monitoring session. */
  hasFocusedLiveStream(): boolean {
    return !!this.getFocusedStreamTileId();
  }

  getFocusedSnapshotEmployeeId(): number | null {
    const tileId = this.getFocusedStreamTileId();
    if (!tileId) return null;
    const t = this.streamManager.getTile(tileId);
    const employeeId = Number(t?.employeeId || t?.employee?.id);
    return employeeId || null;
  }

  isFocusedSnapshotOnCooldown(): boolean {
    const employeeId = this.getFocusedSnapshotEmployeeId();
    if (!employeeId) return false;
    return this.sharedService.isScreenshotRequestOnCooldown(employeeId);
  }

  requestFocusedSnapshot(): void {
    const tileId = this.getFocusedStreamTileId();
    if (tileId) this.requestManualSnapshot(tileId);
  }

  private getFocusedTileEmployee(): any | null {
    const tiles = this.streamManager.getTilesSnapshot();
    if (!tiles) return null;

    const pick = (id: string): any | null => {
      const t = tiles[id];
      if (!t || !t.employee) return null;
      if (t.status === 'empty' || t.status === 'ended') return null;
      return t.employee;
    };

    if (this.mode === 'single') return pick('single');
    if (this.mode === 'patrol') return pick('patrol');

    const layout = this.mode === 'grid'
      ? this.streamManager.gridTileIds(this.gridSize)
      : this.streamManager.wallTileIds(16);

    if (this.selectedTileId && layout.includes(this.selectedTileId)) {
      const t = pick(this.selectedTileId);
      if (t) return t;
    }
    for (const id of layout) {
      const t = pick(id);
      if (t) return t;
    }
    return null;
  }

  /**
   * How many tiles are actively streaming in the current mode. Used by the
   * "View Mode" strip to show a "+N more" hint in grid/wall when several
   * employees are being monitored simultaneously.
   */
  get focusedStreamCount(): number {
    const tiles = this.streamManager.getTilesSnapshot();
    if (!tiles) return 0;
    const layout = this.mode === 'single' ? ['single']
      : this.mode === 'patrol' ? ['patrol']
        : this.mode === 'grid' ? this.streamManager.gridTileIds(this.gridSize)
          : this.streamManager.wallTileIds(16);
    let n = 0;
    for (const id of layout) {
      const t = tiles[id];
      if (t && t.employee && t.status !== 'empty' && t.status !== 'ended') n++;
    }
    return n;
  }

  getModeDescription(): string {
    if (this.mode === 'single') {
      return this.canUseAdvancedLiveModes()
        ? 'Watch one employee'
        : 'Watch one employee (upgrade for grid, wall & patrol)';
    }
    if (this.mode === 'grid') return 'Up to 4 employees';
    if (this.mode === 'wall') return 'Command center wall view';
    return 'Auto-rotate selected employees';
  }

  get patrolPoolEmployees(): any[] {
    const base = (this.employees || []).filter((u) => this.canRequestScreencast(u));
    if (!this.patrolSelectedEmployeeIds.length) return base;
    const picked = new Set(this.patrolSelectedEmployeeIds);
    return base.filter((u) => picked.has(Number(u?.id)));
  }

  get patrolSummaryText(): string {
    const count = this.patrolPoolEmployees.length;
    const noun = count === 1 ? 'employee' : 'employees';
    const scope = this.patrolSelectedEmployeeIds.length ? 'selected roster' : 'all active employees';
    return `Rotating every ${this.patrolIntervalSec}s across ${count} ${noun} (${scope}).`;
  }

  /** Short label for the dense desktop patrol strip (full text in title). */
  get patrolSummaryCompact(): string {
    const count = this.patrolPoolEmployees.length;
    const scope = this.patrolSelectedEmployeeIds.length ? 'selected' : 'all active';
    return `${count} · ${this.patrolIntervalSec}s · ${scope}`;
  }

  isInPatrolSelection(user: any): boolean {
    const id = Number(user?.id);
    if (!id) return false;
    return this.patrolSelectedEmployeeIds.includes(id);
  }

  getStatusLabel(user: any): string {
    const t = this.getUserStatusType(user);
    if (t === 'online') return 'Active';
    if (t === 'idle') return 'Idle';
    if (t === 'invited') return 'Invited';
    if (t === 'auto-logout') return 'Auto logout';
    if (t === 'absent') return 'Absent';
    if (t === 'offline') return 'Offline';
    return 'Unknown';
  }

  getStatusDotClass(user: any): string {
    const t = this.getUserStatusType(user);
    if (t === 'online') return 'online';
    if (t === 'idle') return 'idle';
    if (t === 'invited') return 'invited';
    return 'offline';
  }

  getEmployeeStateLine(user: any): string {
    const status = this.getStatusLabel(user);
    const app = String(user?.meta?.last_application_name || '').trim();
    return app ? `${status} · ${app}` : status;
  }

  getEmployeeActivityLine(user: any): string {
    const statusType = this.getUserStatusType(user);

    if (statusType === 'idle') {
      const idleAgo = this.formatLastActiveAgo(user?.idleStartTime);
      if (idleAgo) return `Idle since ${idleAgo}`;
      const fallback = this.formatLastActiveAgo(user?.lastOnlineAtTz) || this.formatLastActiveAgo(user?.lastOnlineAt);
      if (fallback) return `Last active ${fallback}`;
      return 'Idle';
    }

    if (statusType === 'online') {
      return 'Active now';
    }

    const lastAgo = this.formatLastActiveAgo(user?.lastOnlineAtTz) || this.formatLastActiveAgo(user?.lastOnlineAt);
    if (lastAgo) return `Last active ${lastAgo}`;

    const statusAgo = typeof user?.status === 'string' ? this.formatStatusTimestampAgo(user.status) : '';
    if (statusAgo) return `Last active ${statusAgo}`;

    return 'Not live';
  }

  /** Same UTC handling as Employees table / Dashboard idle tooltips. */
  private formatLastActiveAgo(value: any): string {
    if (value == null || value === '') return '';
    const formatted = this.customtimeAgoPipe.transform(value, 'lastActive');
    return formatted && formatted !== value ? String(formatted) : '';
  }

  private formatStatusTimestampAgo(status: string): string {
    const formatted = this.customtimeAgoPipe.transform(status, 'lastActive');
    return formatted && formatted !== status ? String(formatted) : '';
  }

  hasRenderableTile(tile: LiveTileState | undefined | null): boolean {
    if (!tile) return false;
    return tile.status !== 'empty' && tile.status !== 'ended';
  }

  isModeEmpty(tilesMap: Record<string, LiveTileState>): boolean {
    if (!tilesMap) return true;
    if (this.mode === 'single') return !this.hasRenderableTile(tilesMap['single']);
    if (this.mode === 'patrol') return !this.hasRenderableTile(tilesMap['patrol']);
    if (this.mode === 'grid') {
      return this.streamManager.gridTileIds(this.gridSize).every((id) => !this.hasRenderableTile(tilesMap[id]));
    }
    return this.streamManager.wallTileIds(16).every((id) => !this.hasRenderableTile(tilesMap[id]));
  }

  selectFirstActiveEmployee() {
    const first = (this.filteredEmployees || []).find((u) => this.canRequestScreencast(u))
      || (this.employees || []).find((u) => this.canRequestScreencast(u));
    if (!first) return;
    if (this.isMobileUi) {
      this.mobilePanel = 'stream';
    }
    this.selectedEmployeeId = Number(first?.id) || null;
    this.onEmployeePrimaryAction(first);
  }

  private rebuildTeamMembershipIndex() {
    const next = new Map<string, Set<number>>();
    const toIds = (raw: any): number[] => {
      if (!raw || raw === '') return [];
      return String(raw)
        .replaceAll('~', ',')
        .split(',')
        .map((x) => Number(String(x).trim()))
        .filter((n) => !!n);
    };

    for (const team of (this.teamsListForFilter || [])) {
      const teamName = String(team?.title?.rendered || team?.name || '').trim();
      if (!teamName) continue;
      const key = teamName.toLowerCase();
      const ids = new Set<number>();
      for (const id of toIds(team?.meta?.team_developer)) ids.add(id);
      for (const id of toIds(team?.meta?.team_lead)) ids.add(id);
      for (const id of toIds(team?.meta?.team_manager)) ids.add(id);
      next.set(key, ids);
    }
    this.teamMemberIdsByName = next;
  }

  /**
   * Mirror the Employees table logic: only online/idle should be considered eligible.
   * Other statuses may be timestamps or special strings.
   */
  getUserStatusType(user: any): 'online' | 'idle' | 'offline' | 'absent' | 'invited' | 'auto-logout' | 'none' {
    if (!user) return 'none';
    if (user.status === 'auto-logout') return 'auto-logout';
    if (user.status === 'idle') return 'idle';
    if (user.status === 'online') return 'online';
    if (!user.status || user.status === 'not-using') return 'invited';
    // Anything else is usually a timestamp string or moment-like value
    const statusValue = moment.isMoment(user.status)
      ? user.status.format('YYYY-MM-DD HH:mm:ss')
      : user.status;
    const diff = this.sharedService.getDayDifferenceForAbsent(statusValue);
    return diff > 0 ? 'absent' : 'offline';
  }

  canRequestScreencast(user: any): boolean {
    const t = this.getUserStatusType(user);
    return t === 'online' || t === 'idle';
  }

  private applyFbPresenceSnapshot(fbUsers: any[]) {
    if (!this.employees || !this.employees.length) return;
    if (!fbUsers || !fbUsers.length) return;

    const fbById = new Map<number, any>();
    for (const fb of fbUsers) {
      const id = Number((fb as any)?.id);
      if (id) fbById.set(id, fb);
    }

    let changed = false;
    const next = (this.employees || []).map((cur: any) => {
      const id = Number(cur?.id);
      if (!id) return cur;
      const fb = fbById.get(id);
      if (!fb) return cur;

      // Match UserList update logic (status, idleStartTime)
      const logoutReason = (fb as any)?.logoutReason;
      const logoutTime = logoutReason?.time;
      const lastOnlineAtTz = (fb as any)?.lastOnlineAtTz;
      const lastOnlineAt = (fb as any)?.lastOnlineAt;
      const isLogoutReason = logoutTime && (lastOnlineAtTz || lastOnlineAt)
        ? moment(logoutTime).isSameOrAfter(moment(lastOnlineAtTz || lastOnlineAt))
        : false;

      let status: any = cur.status;
      let idleStartTime: any = null;
      let logoutReasonNext: any = cur.logoutReason;
      let lastAppName: string | undefined = cur?.meta?.last_application_name;

      if (logoutReason && isLogoutReason) {
        status = 'auto-logout';
        logoutReasonNext = logoutReason;
      } else if ((fb as any)?.isIdle === true) {
        // Employees page: treat idle only if still within "online" window (and not auto-logout)
        if (lastOnlineAtTz && this.sharedService.getUserCurrentStatus(lastOnlineAtTz, true) == 'online') {
          status = 'idle';
          if ((fb as any)?.idleStartTime) idleStartTime = (fb as any)?.idleStartTime;
        } else if (lastOnlineAtTz) {
          status = this.sharedService.getUserCurrentStatus(lastOnlineAtTz, true);
        } else if (lastOnlineAt) {
          status = this.sharedService.getUserCurrentStatus(lastOnlineAt, false);
        } else {
          status = 'not-using';
        }
      } else if (lastOnlineAtTz) {
        status = this.sharedService.getUserCurrentStatus(lastOnlineAtTz, true);
      } else if (lastOnlineAt) {
        status = this.sharedService.getUserCurrentStatus(lastOnlineAt, false);
      } else {
        status = 'not-using';
      }

      // Update "last application" display if available
      if ((fb as any)?.ssPeriodic?.applicationName) {
        lastAppName = (fb as any).ssPeriodic.applicationName;
      } else if ((fb as any)?.ssAppChanged?.applicationName) {
        lastAppName = (fb as any).ssAppChanged.applicationName;
      }

      const nextCur = {
        ...cur,
        status,
        idleStartTime: status === 'idle' ? idleStartTime : null,
        lastOnlineAtTz: lastOnlineAtTz ?? cur.lastOnlineAtTz,
        lastOnlineAt: lastOnlineAt ?? cur.lastOnlineAt,
        logoutReason: logoutReasonNext,
        // Keep agent version/runtime so Rust Beta badge can detect 5.x agents.
        appInfo: (fb as any)?.appInfo ?? cur.appInfo,
        osInfo: (fb as any)?.osInfo ?? cur.osInfo,
        meta: { ...(cur?.meta || {}), ...(lastAppName ? { last_application_name: lastAppName } : {}) },
      };

      if (
        nextCur.status !== cur.status ||
        nextCur.idleStartTime !== cur.idleStartTime ||
        nextCur.lastOnlineAtTz !== cur.lastOnlineAtTz ||
        nextCur.lastOnlineAt !== cur.lastOnlineAt ||
        nextCur.logoutReason !== cur.logoutReason ||
        nextCur.appInfo !== cur.appInfo ||
        (nextCur.meta?.last_application_name !== cur?.meta?.last_application_name)
      ) {
        changed = true;
      }
      return nextCur;
    });

    if (changed) {
      this.employees = next;
      this.cdr.markForCheck();
    }
    this.autoStartStreamIfSelected();
  }

  private autoStartStreamIfSelected() {
    if (!this.pendingAutoStartEmployeeId) return;

    const user = (this.employees || []).find((u: any) => Number(u?.id) === this.pendingAutoStartEmployeeId);
    if (!user) return;

    if (!this.canRequestScreencast(user)) return;

    const idToStart = this.pendingAutoStartEmployeeId;
    this.pendingAutoStartEmployeeId = null;

    if (this.isEmployeeStreaming(user)) return;

    if (this.selectedEmployeeId === idToStart) {
      this.onEmployeePrimaryAction(user);
      this.cdr.markForCheck();
    }
  }

  private bootstrapEmployeesFast() {
    const seededFromShared = Array.isArray(this.sharedService.userList) && this.sharedService.userList.length
      ? this.sharedService.userList
      : null;
    if (seededFromShared) {
      this.applyEmployeeList(seededFromShared);
      return;
    }

    const cached = this.readEmployeeCache();
    if (cached?.length) {
      this.applyEmployeeList(cached);
    }
  }

  private async applyEmployeeList(users: any) {
    const incoming = Array.isArray(users) ? users : [];
    if (!incoming.length) return;

    // Preserve transient local status from Firebase enrichment if the same user exists.
    const prevById = new Map<number, any>();
    for (const u of this.employees || []) {
      const id = Number(u?.id);
      if (id) prevById.set(id, u);
    }

    const next = incoming.map((u: any) => {
      const id = Number(u?.id);
      const prev = id ? prevById.get(id) : null;
      if (!prev) return u;
      const mergedStatus = prev?.status ?? u?.status;
      return {
        ...u,
        status: mergedStatus,
        idleStartTime: mergedStatus === 'idle' ? (prev?.idleStartTime ?? u?.idleStartTime) : null,
        lastOnlineAtTz: prev?.lastOnlineAtTz ?? u?.lastOnlineAtTz,
        lastOnlineAt: prev?.lastOnlineAt ?? u?.lastOnlineAt,
        logoutReason: prev?.logoutReason ?? u?.logoutReason,
        appInfo: prev?.appInfo ?? u?.appInfo,
        osInfo: prev?.osInfo ?? u?.osInfo,
        meta: {
          ...(u?.meta || {}),
          ...(prev?.meta?.last_application_name ? { last_application_name: prev.meta.last_application_name } : {}),
        },
      };
    });

    const withTeams = await this.assignTeamsAccordingDashboardRules(next);
    this.employees = (withTeams || []).filter((u: any) => u && u.id != null);
    // Merge into the existing roster by id — do not replace with a partial/holey
    // list or screenshot success toasts on this page get dropped.
    if (this.employees.length) {
      const byId = new Map<string, any>();
      for (const u of this.sharedService.userList || []) {
        if (u?.id != null) byId.set(String(u.id), u);
      }
      for (const u of this.employees) {
        byId.set(String(u.id), { ...(byId.get(String(u.id)) || {}), ...u });
      }
      this.sharedService.userList = Array.from(byId.values());
    }
    this.writeEmployeeCache(withTeams);

    // If we already have Firebase snapshot, apply immediately for parity.
    if (this.lastFbUsersSnapshot?.length) {
      this.applyFbPresenceSnapshot(this.lastFbUsersSnapshot);
    }
    this.autoStartStreamIfSelected();
  }

  private async assignTeamsAccordingDashboardRules(users: any[]): Promise<any[]> {
    if (!Array.isArray(users) || !users.length) return users || [];
    if (!Array.isArray(this.teamsListForFilter) || !this.teamsListForFilter.length) return users;
    try {
      const cloned = users.map((u: any) => ({
        ...u,
        selectedTeam: Array.isArray(u?.selectedTeam) ? [...u.selectedTeam] : u?.selectedTeam,
      }));
      const assigned = await this.sharedService.assignTeamsAccordingList(cloned, this.teamsListForFilter);
      return Array.isArray(assigned) ? assigned : users;
    } catch {
      return users;
    }
  }

  private employeeCacheKey(): string {
    const companyId = this.tokenEncryption.getEncryptedItem<any>('logedInUser')?.company_id ?? '0';
    return `live_monitoring_employees_${companyId}`;
  }

  private readEmployeeCache(): any[] {
    try {
      const raw = localStorage.getItem(this.employeeCacheKey());
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      const ts = Number(parsed?.ts || 0);
      const data = Array.isArray(parsed?.data) ? parsed.data : [];
      if (!ts || !data.length) return [];
      if ((Date.now() - ts) > this.employeeCacheTtlMs) return [];
      return data;
    } catch {
      return [];
    }
  }

  private writeEmployeeCache(users: any[]) {
    try {
      const safe = (users || []).map((u: any) => ({
        id: u?.id,
        name: u?.name,
        display_name: u?.display_name,
        email: u?.email,
        user_email: u?.user_email,
        status: u?.status,
        idleStartTime: u?.idleStartTime,
        lastOnlineAtTz: u?.lastOnlineAtTz,
        lastOnlineAt: u?.lastOnlineAt,
        logoutReason: u?.logoutReason,
        meta: {
          last_application_name: u?.meta?.last_application_name || '',
          callingid: u?.meta?.callingid || '',
        },
      }));
      localStorage.setItem(this.employeeCacheKey(), JSON.stringify({ ts: Date.now(), data: safe }));
    } catch { }
  }

  get employeeCounters() {
    const list = this.employees || [];
    let active = 0;
    let idle = 0;
    let offline = 0;
    for (const u of list) {
      const t = this.getUserStatusType(u);
      if (t === 'online') active++;
      else if (t === 'idle') idle++;
      else if (t === 'offline' || t === 'absent') offline++;
    }
    return { active, idle, offline };
  }

  get filteredEmployeeCounters() {
    const list = this.filteredEmployees || [];
    let active = 0;
    let idle = 0;
    let offline = 0;
    for (const u of list) {
      const t = this.getUserStatusType(u);
      if (t === 'online') active++;
      else if (t === 'idle') idle++;
      else if (t === 'offline' || t === 'absent') offline++;
    }
    return { total: list.length, active, idle, offline };
  }

  setMode(next: LiveMode) {
    if (this.mode === next) return;
    if (next !== 'single' && !this.canUseAdvancedLiveModes()) {
      this.promptAdvancedLiveUpgrade(this.advancedModeLabel(next));
      return;
    }
    const prev = this.mode;
    this.stopPatrol();

    // Single -> Grid: carry active single stream into first grid tile.
    if (prev === 'single' && next === 'grid') {
      const moved = this.streamManager.moveSession('single', 'g1', 'grid', this.gridSize);
      this.mode = next;
      this.selectedTileId = moved ? 'g1' : null;
      this.syncSidebarToFocusedTile();
      return;
    }

    // Single -> Wall: carry the single stream into the first wall slot.
    if (prev === 'single' && next === 'wall') {
      const moved = this.streamManager.moveSession('single', 'w1', 'wall', 16);
      this.mode = next;
      this.selectedTileId = moved ? 'w1' : null;
      this.syncSidebarToFocusedTile();
      return;
    }

    // Single -> Patrol: keep watching the current employee but rotate from
    // the patrol tile so the user doesn't lose their context.
    if (prev === 'single' && next === 'patrol') {
      const moved = this.streamManager.moveSession('single', 'patrol', 'patrol', 1);
      this.mode = next;
      this.selectedTileId = moved ? 'patrol' : null;
      this.syncSidebarToFocusedTile();
      return;
    }

    // Grid/Wall -> Single: keep focused active tile if possible, then close others.
    if ((prev === 'grid' || prev === 'wall') && next === 'single') {
      const candidateOrder = this.selectedTileId
        ? [this.selectedTileId, 'g1', 'g2', 'g3', 'g4', ...this.streamManager.wallTileIds(16)]
        : ['g1', 'g2', 'g3', 'g4', ...this.streamManager.wallTileIds(16)];
      const src = this.streamManager.getFirstActiveTileId(candidateOrder);
      if (src && src !== 'single') {
        this.streamManager.moveSession(src, 'single', 'single', 1);
      }
      this.streamManager.stopAllExcept(['single'], 'mode_changed');
      this.mode = next;
      this.selectedTileId = this.streamManager.isTileActive('single') ? 'single' : null;
      this.syncSidebarToFocusedTile();
      return;
    }

    // Grid <-> Wall: preserve as many active streams as the target layout
    // can hold (16 -> 4 may drop some, 4 -> 16 keeps them all).
    if (prev === 'grid' && next === 'wall') {
      const sources = this.streamManager.gridTileIds(this.gridSize);
      const targets = this.streamManager.wallTileIds(16);
      const firstTarget = this.streamManager.migrateSessions(sources, targets, 'wall', 16);
      this.mode = next;
      this.selectedTileId = firstTarget || null;
      this.syncSidebarToFocusedTile();
      return;
    }
    if (prev === 'wall' && next === 'grid') {
      const sources = this.streamManager.wallTileIds(16);
      const targets = this.streamManager.gridTileIds(this.gridSize);
      const firstTarget = this.streamManager.migrateSessions(sources, targets, 'grid', this.gridSize);
      this.streamManager.stopAllExcept(targets, 'mode_changed');
      this.mode = next;
      this.selectedTileId = firstTarget || null;
      this.syncSidebarToFocusedTile();
      return;
    }

    // Grid/Wall -> Patrol: continue rotating from the focused employee.
    if ((prev === 'grid' || prev === 'wall') && next === 'patrol') {
      const layout = prev === 'grid'
        ? this.streamManager.gridTileIds(this.gridSize)
        : this.streamManager.wallTileIds(16);
      const focus = this.selectedTileId && layout.includes(this.selectedTileId) ? this.selectedTileId : null;
      const src = focus && this.streamManager.isTileActive(focus)
        ? focus
        : this.streamManager.getFirstActiveTileId(layout);
      if (src) this.streamManager.moveSession(src, 'patrol', 'patrol', 1);
      this.streamManager.stopAllExcept(['patrol'], 'mode_changed');
      this.mode = next;
      this.selectedTileId = this.streamManager.isTileActive('patrol') ? 'patrol' : null;
      this.syncSidebarToFocusedTile();
      return;
    }

    // Patrol -> Single/Grid/Wall: preserve the currently-rotating employee
    // in the new mode so the screen does not go blank during a mode change.
    if (prev === 'patrol' && next === 'single') {
      this.streamManager.moveSession('patrol', 'single', 'single', 1);
      this.mode = next;
      this.selectedTileId = this.streamManager.isTileActive('single') ? 'single' : null;
      this.syncSidebarToFocusedTile();
      return;
    }
    if (prev === 'patrol' && next === 'grid') {
      this.streamManager.moveSession('patrol', 'g1', 'grid', this.gridSize);
      this.mode = next;
      this.selectedTileId = this.streamManager.isTileActive('g1') ? 'g1' : null;
      this.syncSidebarToFocusedTile();
      return;
    }
    if (prev === 'patrol' && next === 'wall') {
      this.streamManager.moveSession('patrol', 'w1', 'wall', 16);
      this.mode = next;
      this.selectedTileId = this.streamManager.isTileActive('w1') ? 'w1' : null;
      this.syncSidebarToFocusedTile();
      return;
    }

    // Defensive fallback for any unhandled transition.
    this.streamManager.stopAllStreams('mode_changed');
    this.selectedTileId = null;
    this.mode = next;
    this.syncSidebarToFocusedTile();
  }

  /**
   * Aligns the sidebar highlight with whatever is actually being viewed in
   * the workspace. Without this, switching modes can leave the left list
   * highlighting an employee that has nothing to do with the current screen.
   */
  private syncSidebarToFocusedTile() {
    const focused = this.getFocusedTileEmployee();
    const id = Number(focused?.id);
    if (id) {
      this.selectedEmployeeId = id;
    } else if (this.mode !== 'patrol') {
      // In grid/wall/single with nothing streaming, drop the stale highlight
      // so the user gets a clean slate rather than a misleading selection.
      this.selectedEmployeeId = null;
    }
    this.updateQueryParams();
  }

  selectEmployee(user: any) {
    this.pendingAutoStartEmployeeId = null;
    const id = Number(user?.id);
    if (!id) return;
    this.selectedEmployeeId = id;
    this.tryFillPendingEmptySlot(user);
    if (this.isMobileUi) {
      this.mobilePanel = 'stream';
      if (this.canRequestScreencast(user)) {
        this.onEmployeePrimaryAction(user);
        this.updateQueryParams();
        this.cdr.markForCheck();
        return;
      }
    }
    this.updateQueryParams();
    this.cdr.markForCheck();
  }

  private isEmptyLayoutTile(tileId: string | null): boolean {
    if (!tileId) return false;
    const t = this.streamManager.getTile(tileId);
    if (!t) return true;
    return t.status === 'empty' || t.status === 'ended' || !t.employeeId;
  }

  /** After picking someone in the sidebar, fill the empty slot they clicked earlier. */
  private tryFillPendingEmptySlot(user: any) {
    if (this.mode !== 'grid' && this.mode !== 'wall') return;
    const tileId = this.selectedTileId;
    if (!tileId || !this.isEmptyLayoutTile(tileId)) return;
    if (!user || !this.canRequestScreencast(user)) return;
    if (this.isEmployeeInCurrentLayout(user)) return;

    if (this.mode === 'grid' && this.streamManager.gridTileIds(this.gridSize).includes(tileId)) {
      this.addToGrid(user, tileId);
      return;
    }
    if (this.mode === 'wall' && this.streamManager.wallTileIds(16).includes(tileId)) {
      this.addToWall(user, tileId);
    }
  }

  private openEmployeePickerForSlot(tileId: string) {
    this.selectedTileId = tileId;
    if (this.isLeftPaneCollapsed && !this.isMobileUi) {
      this.isLeftPaneCollapsed = false;
      try {
        localStorage.setItem('liveMonitoring.leftPaneCollapsed', 'false');
      } catch { }
    }
    this.cdr.markForCheck();
  }

  private handleEmptySlotClick(tileId: string) {
    this.openEmployeePickerForSlot(tileId);
  }

  onEmployeePrimaryAction(user: any) {
    this.pendingAutoStartEmployeeId = null;
    const id = Number(user?.id);
    if (!id) return;
    if (!this.canRequestScreencast(user)) return;
    this.selectedEmployeeId = id;
    if (this.isMobileUi) {
      this.mobilePanel = 'stream';
    }

    if (this.mode === 'single') {
      this.openInSingle(user);
      return;
    }
    if (this.mode === 'grid') {
      const preferred = this.isEmptyLayoutTile(this.selectedTileId) ? this.selectedTileId : null;
      this.addToGrid(user, preferred);
      this.cdr.markForCheck();
      return;
    }
    if (this.mode === 'wall') {
      const preferred = this.isEmptyLayoutTile(this.selectedTileId) ? this.selectedTileId : null;
      this.addToWall(user, preferred);
      this.cdr.markForCheck();
      return;
    }
    this.togglePatrolSelection(user);
  }

  togglePatrolSelection(user: any) {
    if (!this.canUseAdvancedLiveModes()) {
      this.promptAdvancedLiveUpgrade('Patrol mode');
      return;
    }
    const id = Number(user?.id);
    if (!id) return;
    if (!this.canRequestScreencast(user)) return;
    this.mode = 'patrol';
    if (this.isInPatrolSelection(user)) {
      this.patrolSelectedEmployeeIds = this.patrolSelectedEmployeeIds.filter((x) => x !== id);
    } else {
      this.patrolSelectedEmployeeIds = [...this.patrolSelectedEmployeeIds, id];
    }
    // Keep patrol index in bounds whenever roster changes.
    if (this.patrolIndex >= this.patrolPoolEmployees.length) this.patrolIndex = 0;
    if (this.patrolRunning && !this.patrolPoolEmployees.length) {
      this.stopPatrol();
      this.streamManager.stopStream('patrol', 'patrol_stopped');
    }
    this.selectedEmployeeId = id;
    
    this.updateQueryParams();

    // If patrol isn't running yet, show immediate preview for last selected.
    if (!this.patrolRunning && this.isInPatrolSelection(user)) {
      this.watchInPatrol(user);
    }
  }

  removeFromPatrolSelection(employeeId: number) {
    const id = Number(employeeId);
    if (!id) return;
    this.patrolSelectedEmployeeIds = this.patrolSelectedEmployeeIds.filter((x) => x !== id);
    if (this.patrolIndex >= this.patrolPoolEmployees.length) this.patrolIndex = 0;
    if (this.patrolRunning && !this.patrolPoolEmployees.length) {
      this.stopPatrol();
      this.streamManager.stopStream('patrol', 'patrol_stopped');
    }
    this.updateQueryParams();
  }

  clearPatrolSelection() {
    this.patrolSelectedEmployeeIds = [];
    this.patrolIndex = 0;
    if (this.patrolRunning && !this.patrolPoolEmployees.length) {
      this.stopPatrol();
      this.streamManager.stopStream('patrol', 'patrol_stopped');
    }
    this.updateQueryParams();
  }

  onPatrolIntervalChange(next: any) {
    const parsed = Number(next);
    if (parsed !== 10 && parsed !== 20 && parsed !== 30 && parsed !== 60) return;
    this.patrolIntervalSec = parsed;
    if (this.patrolRunning) {
      if (this.patrolTimer) clearInterval(this.patrolTimer);
      this.patrolTimer = setInterval(() => this.rotatePatrol(false), this.patrolIntervalSec * 1000);
    }
    this.updateQueryParams();
  }

  openInSingle(user: any) {
    const id = Number(user?.id);
    if (!id) return;
    if (!this.canRequestScreencast(user)) return;
    this.mode = 'single';
    this.selectedEmployeeId = id;
    this.streamManager.startStream({
      tileId: 'single',
      employee: user,
      mode: 'single',
      layoutSize: 1,
      endReasonOnReplace: 'employee_switched',
    });
    this.selectedTileId = 'single';
    this.cdr.markForCheck();
  }

  private resolveGridTargetTile(preferredTileId?: string | null): string | null {
    const ids = this.streamManager.gridTileIds(this.gridSize);
    if (preferredTileId && ids.includes(preferredTileId)) return preferredTileId;
    const open = this.streamManager.pickNextFreeTile(this.gridSize);
    if (open) return open;
    const selected = this.selectedTileId;
    if (selected && ids.includes(selected)) return selected;
    return ids[0] || null;
  }

  private resolveWallTargetTile(preferredTileId?: string | null): string | null {
    const ids = this.streamManager.wallTileIds(16);
    if (preferredTileId && ids.includes(preferredTileId)) return preferredTileId;
    const open = this.streamManager.pickNextFreeWallTile(16);
    if (open) return open;
    const selected = this.selectedTileId;
    if (selected && ids.includes(selected)) return selected;
    return ids[0] || null;
  }

  getEmptySlotHint(): string {
    return 'Add an employee from the employee list.';
  }

  addToGrid(user: any, preferredTileId?: string | null) {
    if (!this.canUseAdvancedLiveModes()) {
      this.promptAdvancedLiveUpgrade('Grid view');
      return;
    }
    const id = Number(user?.id);
    if (!id) return;
    if (!this.canRequestScreencast(user)) return;
    this.mode = 'grid';
    const target = this.resolveGridTargetTile(preferredTileId);
    if (!target) return;
    this.streamManager.startStream({
      tileId: target,
      employee: user,
      mode: 'grid',
      layoutSize: this.gridSize,
      endReasonOnReplace: 'tile_replaced',
    });
    this.selectedTileId = target;
    this.selectedEmployeeId = id;
    this.cdr.markForCheck();
  }

  addToWall(user: any, preferredTileId?: string | null) {
    if (!this.canUseAdvancedLiveModes()) {
      this.promptAdvancedLiveUpgrade('Video wall');
      return;
    }
    const id = Number(user?.id);
    if (!id) return;
    if (!this.canRequestScreencast(user)) return;
    this.mode = 'wall';
    const target = this.resolveWallTargetTile(preferredTileId);
    if (!target) return;
    this.streamManager.startStream({
      tileId: target,
      employee: user,
      mode: 'wall',
      layoutSize: 16,
      endReasonOnReplace: 'tile_replaced',
    });
    this.selectedTileId = target;
    this.selectedEmployeeId = id;
    this.cdr.markForCheck();
  }

  watchInPatrol(user: any) {
    if (!this.canUseAdvancedLiveModes()) {
      this.promptAdvancedLiveUpgrade('Patrol mode');
      return;
    }
    const id = Number(user?.id);
    if (!id) return;
    if (!this.canRequestScreencast(user)) return;
    this.mode = 'patrol';
    this.stopPatrol();
    this.streamManager.startStream({
      tileId: 'patrol',
      employee: user,
      mode: 'patrol',
      layoutSize: 1,
      endReasonOnReplace: 'patrol_next',
    });
    this.selectedEmployeeId = id;
    this.selectedTileId = 'patrol';
  }

  /** Patrol was paused (tile still active) — show Resume, not Start. */
  get showPatrolResume(): boolean {
    return this.mode === 'patrol' && !this.patrolRunning && this.selectedTileId === 'patrol';
  }

  get showPatrolStart(): boolean {
    return this.mode === 'patrol' && !this.patrolRunning && !this.showPatrolResume;
  }

  get showPatrolStepControls(): boolean {
    return this.mode === 'patrol' && (this.patrolRunning || this.showPatrolResume);
  }

  get showPatrolStop(): boolean {
    return this.mode === 'patrol' && (this.patrolRunning || this.showPatrolResume);
  }

  get mobileModeHint(): string {
    if (this.mode === 'single') return 'Single mode: one desktop in focus.';
    if (this.mode === 'grid') return 'Grid mode: compare multiple desktops at once.';
    if (this.mode === 'wall') return 'Wall mode: monitor many desktops quickly.';
    return this.patrolRunning
      ? `Patrol running: rotates every ${this.patrolIntervalSec} sec.`
      : 'Patrol mode: choose roster and start auto-rotation.';
  }

  getPrimaryActionLabel(): string {
    if (this.mode === 'single') return 'View';
    if (this.mode === 'grid') return 'Add to grid';
    if (this.mode === 'wall') return 'Add to wall';
    return 'Select patrol';
  }

  getSecondaryActionLabel(): string {
    return this.mode === 'single' ? 'Viewing mode' : 'Open single';
  }

  onSecondaryAction(user: any) {
    if (this.mode === 'single') return;
    this.openInSingle(user);
  }

  getActiveTilesForEmployee(user: any): string[] {
    const id = Number(user?.id);
    if (!id) return [];
    return this.streamManager.getActiveTileIdsForEmployee(id);
  }

  isEmployeeStreaming(user: any): boolean {
    return this.getActiveTilesForEmployee(user).length > 0;
  }

  /** True when this employee already occupies a tile in the current grid/wall layout. */
  isEmployeeInCurrentLayout(user: any): boolean {
    const id = Number(user?.id);
    if (!id) return false;
    const activeIds = this.streamManager.getActiveTileIdsForEmployee(id);
    if (!activeIds.length) return false;
    if (this.mode === 'grid') {
      const layout = new Set(this.streamManager.gridTileIds(this.gridSize));
      return activeIds.some((tileId) => layout.has(tileId));
    }
    if (this.mode === 'wall') {
      const layout = new Set(this.streamManager.wallTileIds(16));
      return activeIds.some((tileId) => layout.has(tileId));
    }
    return false;
  }

  fillWallFromActive() {
    if (!this.canUseAdvancedLiveModes()) {
      this.promptAdvancedLiveUpgrade('Video wall');
      return;
    }
    this.mode = 'wall';
    this.streamManager.stopAllStreams('mode_changed');
    const active = (this.employees || []).filter(u => this.canRequestScreencast(u));
    const picked = active.slice(0, 16);
    const tileIds = this.streamManager.wallTileIds(16);
    for (let i = 0; i < tileIds.length; i++) {
      const emp = picked[i];
      if (!emp) break;
      this.streamManager.startStream({
        tileId: tileIds[i],
        employee: emp,
        mode: 'wall',
        layoutSize: 16,
        endReasonOnReplace: 'tile_replaced',
      });
    }
    this.selectedTileId = tileIds[0] || null;
    this.selectedEmployeeId = picked[0] ? Number(picked[0].id) || null : null;
  }

  fillGridFromActive() {
    if (!this.canUseAdvancedLiveModes()) {
      this.promptAdvancedLiveUpgrade('Grid view');
      return;
    }
    this.mode = 'grid';
    this.streamManager.stopAllStreams('mode_changed');
    const active = (this.employees || []).filter((u) => this.canRequestScreencast(u));
    const tileIds = this.streamManager.gridTileIds(this.gridSize);
    const picked = active.slice(0, tileIds.length);
    for (let i = 0; i < tileIds.length; i++) {
      const emp = picked[i];
      if (!emp) break;
      this.streamManager.startStream({
        tileId: tileIds[i],
        employee: emp,
        mode: 'grid',
        layoutSize: this.gridSize,
        endReasonOnReplace: 'tile_replaced',
      });
    }
    this.selectedTileId = tileIds[0] || null;
    this.selectedEmployeeId = picked[0] ? Number(picked[0].id) || null : null;
    this.cdr.markForCheck();
  }

  selectTile(tileId: string) {
    this.selectedTileId = tileId;
    const t = this.streamManager.getTile(tileId);
    const empId = Number(t?.employee?.id || t?.employeeId);
    if (empId) {
      this.selectedEmployeeId = empId;
      this.cdr.markForCheck();
      return;
    }

    if (this.mode === 'grid' || this.mode === 'wall') {
      this.handleEmptySlotClick(tileId);
      return;
    }

    this.cdr.markForCheck();
  }

  stopTile(tileId: string) {
    const mode = this.mode;
    const layoutTileIds =
      mode === 'wall'
        ? this.streamManager.wallTileIds(16)
        : mode === 'grid'
          ? this.streamManager.gridTileIds(this.gridSize)
          : null;

    const keepEmployeeId =
      this.selectedTileId && this.selectedTileId !== tileId
        ? this.streamManager.getTile(this.selectedTileId)?.employeeId ?? null
        : null;

    this.streamManager.stopStream(tileId, 'tile_removed');

    if (layoutTileIds) {
      this.streamManager.compactLayoutTiles(
        layoutTileIds,
        mode as 'grid' | 'wall',
        mode === 'wall' ? 16 : this.gridSize
      );
    }

    if (this.selectedTileId === tileId || keepEmployeeId) {
      if (keepEmployeeId && layoutTileIds) {
        const ids = this.streamManager.getActiveTileIdsForEmployee(keepEmployeeId);
        this.selectedTileId =
          ids.find((id) => layoutTileIds.includes(id)) ??
          this.streamManager.getFirstActiveTileId(layoutTileIds);
      } else {
        this.selectedTileId = layoutTileIds
          ? this.streamManager.getFirstActiveTileId(layoutTileIds)
          : null;
      }
      this.syncSidebarToFocusedTile();
    }

    this.cdr.markForCheck();
  }

  private clearScreenshotRequestTimeout(employeeId: number) {
    const timeoutId = this.screenshotTimeoutHandles.get(employeeId);
    if (timeoutId != null) {
      clearTimeout(timeoutId);
      this.screenshotTimeoutHandles.delete(employeeId);
    }
  }

  private clearAllScreenshotRequestTimeouts() {
    for (const id of [...this.screenshotTimeoutHandles.keys()]) {
      this.clearScreenshotRequestTimeout(id);
    }
  }

  private scheduleScreenshotRequestTimeout(
    dbPath: string,
    employeeId: number,
    userName: string
  ) {
    this.clearScreenshotRequestTimeout(employeeId);

    const timeoutId = setTimeout(() => {
      this.screenshotTimeoutHandles.delete(employeeId);

      this.db.database
        .ref(`${dbPath}`)
        .child(`${employeeId}`)
        .get()
        .then((res) => {
          const val = res.val();
          // Soft failure toast only — do not write ssRequest:false (agent ack race).
          if (!(val?.ssRequest == null)) {
            return;
          }
          this.sharedService.showToast({
            type: 'Cant get screenshot',
            userName,
            userId: employeeId,
          });
        })
        .catch(() => {});
    }, this.screenshotRequestTimeoutMs);

    this.screenshotTimeoutHandles.set(employeeId, timeoutId);
  }

  /**
   * v2 snapshot implementation (reuse-first):
   * trigger the existing screenshot request flow via Firebase.
   *
   * Note: this does not upload a frame from the WebRTC stream; it reuses the
   * existing product screenshot capture/storage pipeline.
   */
  requestManualSnapshot(tileId: string) {
    const t = this.streamManager.getTile(tileId);
    const emp = t?.employee;
    const employeeId = Number(t?.employeeId || emp?.id);
    if (!employeeId) return;

    if (!navigator.onLine) {
      return;
    }

    if (!this.sharedService.canTriggerScreenshotRequest(employeeId)) {
      return;
    }

    const dbPath = this.tokenEncryption.getEncryptedItem<string>('dbPath') as string;
    if (!dbPath) {
      return;
    }

    const userName = emp?.name || emp?.display_name || `User ${employeeId}`;
    const canTrigger =
      t?.status === 'live' ||
      emp?.status === 'online' ||
      emp?.status === 'idle' ||
      this.canRequestScreencast(emp);

    this.sharedService.showToast({
      type: 'Requesting Screenshot',
      userName,
      userId: employeeId,
    });

    // Always arm the 20s failure toast (same as Employees list), then trigger Firebase when allowed.
    this.scheduleScreenshotRequestTimeout(dbPath, employeeId, userName);

    if (!canTrigger) {
      return;
    }

    this.sharedService.startOnDemandScreenshotRequest({
      employeeId,
      dbPath,
      source: 'live_monitoring',
    });
  }

  async startPatrol() {
    if (!this.canUseAdvancedLiveModes()) {
      this.promptAdvancedLiveUpgrade('Patrol mode');
      return;
    }
    this.mode = 'patrol';
    const pool = this.patrolPoolEmployees;
    if (!pool.length) return;
    this.patrolRunning = true;
    if (this.patrolIndex >= pool.length) this.patrolIndex = 0;
    await this.rotatePatrol(true);
    this.patrolTimer = setInterval(() => this.rotatePatrol(false), this.patrolIntervalSec * 1000);
  }

  stopPatrol() {
    this.patrolRunning = false;
    if (this.patrolTimer) clearInterval(this.patrolTimer);
    this.patrolTimer = null;
  }

  pausePatrol() {
    if (!this.patrolRunning) return;
    this.stopPatrol();
  }

  resumePatrol() {
    if (!this.canUseAdvancedLiveModes()) {
      this.promptAdvancedLiveUpgrade('Patrol mode');
      return;
    }
    if (this.patrolRunning) return;
    if (this.mode !== 'patrol') this.mode = 'patrol';
    this.patrolRunning = true;
    this.patrolTimer = setInterval(() => this.rotatePatrol(false), this.patrolIntervalSec * 1000);
  }

  nextPatrol() {
    this.rotatePatrol(false);
  }

  prevPatrol() {
    this.patrolIndex = Math.max(0, this.patrolIndex - 2);
    this.rotatePatrol(false);
  }

  private async rotatePatrol(isFirst: boolean) {
    const pool = this.patrolPoolEmployees;
    if (!pool.length) return;

    if (!isFirst) {
      // must cleanup before moving next
      this.streamManager.stopStream('patrol', 'patrol_next');
    }

    if (this.patrolIndex >= pool.length) this.patrolIndex = 0;
    const emp = pool[this.patrolIndex++];
    if (!emp) return;

    this.streamManager.startStream({
      tileId: 'patrol',
      employee: emp,
      mode: 'patrol',
      layoutSize: 1,
      endReasonOnReplace: 'patrol_next',
    });
    this.selectedEmployeeId = Number(emp?.id) || null;
    this.selectedTileId = 'patrol';
  }

  trackByUserId = (_: number, u: any) => u?.id;
  trackByTileId = (_: number, t: LiveTileState) => t?.tileId;
}

